import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';
import { 
  Syringe,
  CheckCircle2,
  AlertTriangle,
  Activity,
  Download,
  RotateCcw,
  Sparkles,
  Loader2,
  Info,
  MapPin,
  Wrench,
  Shield,
  Eye
} from 'lucide-react';

import { API_URL } from '../config';

export default function VoieIntraosseusePage() {
  const { token } = useAuth();
  const printRef = useRef(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem('voie-intraosseuse');
      return saved ? JSON.parse(saved) : {
        siteChoisi: null,
        tailleTrocart: null,
        indications: [],
        etapesTechnique: {},
        complications: [],
        controles: {}
      };
    } catch {
      return {
        siteChoisi: null,
        tailleTrocart: null,
        indications: [],
        etapesTechnique: {},
        complications: [],
        controles: {}
      };
    }
  });

  useEffect(() => {
    localStorage.setItem('voie-intraosseuse', JSON.stringify(formData));
  }, [formData]);

  const handleReset = () => {
    if (window.confirm('Réinitialiser le formulaire ?')) {
      localStorage.removeItem('voie-intraosseuse');
      window.location.reload();
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Génération du PDF...');
      const opt = {
        margin: 10,
        filename: `Voie-Intraosseuse-${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      await html2pdf().set(opt).from(printRef.current).save();
      toast.success('PDF exporté');
    } catch (error) {
      toast.error('Erreur export PDF');
    }
  };

  const handleAnalyzeAI = async () => {
    try {
      setIsAnalyzing(true);
      toast.info('Analyse IA en cours...');

      const response = await fetch(`${API_URL}/api/ai/analyze-voie-io`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Erreur analyse');
      
      const data = await response.json();
      setAiAnalysis(data);
      toast.success('Analyse terminée');
    } catch (error) {
      toast.error('Erreur analyse IA');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleCheckEtape = (etape) => {
    setFormData(prev => ({
      ...prev,
      etapesTechnique: {
        ...prev.etapesTechnique,
        [etape]: !prev.etapesTechnique[etape]
      }
    }));
  };

  const sites = [
    { 
      id: 'tibia_proximal', 
      nom: 'Tibia proximal',
      repere: 'Méplat interne de la TTA à 90° du plan cutané',
      detail: '1 cm en dessous et en dedans de la tubérosité tibiale'
    },
    { 
      id: 'malleole', 
      nom: 'Malléole interne',
      repere: '4-5 cm au-dessus de la pointe de la malléole interne',
      detail: 'Face interne de la jambe'
    },
    { 
      id: 'tete_humerale', 
      nom: 'Tête humérale',
      repere: 'Ligne processus coracoïde-acromion',
      detail: 'À mi-chemin : 2 cm vers le bas + 2 cm en avant'
    }
  ];

  const etapesTechnique = [
    { id: '1', label: 'Préparation du matériel (trocart, désinfectant, gants)' },
    { id: '2', label: 'Asepsie cutanée rigoureuse' },
    { id: '3', label: 'Anesthésie locale (si patient conscient) - Lidocaïne 2%' },
    { id: '4', label: 'Positionnement correct du membre' },
    { id: '5', label: 'Repérage du site d\'insertion' },
    { id: '6', label: 'Insertion du trocart à 90° de la peau' },
    { id: '7', label: 'Activation du système motorisé jusqu\'au « pop »' },
    { id: '8', label: 'Retrait du mandrin' },
    { id: '9', label: 'Vérification : aspiration de moelle osseuse' },
    { id: '10', label: 'Connexion au système de perfusion' },
    { id: '11', label: 'Flush NaCl 0,9% 10mL et vérification absence de fuite' },
    { id: '12', label: 'Fixation et pansement' }
  ];

  const complications = [
    'Fracture osseuse',
    'Lésion du cartilage de croissance',
    'Embolie graisseuse',
    'Syndrome des loges',
    'Infections locales',
    'Septicémie'
  ];

  const pointsASavoir = [
    'Les doses à utiliser sont identiques à celles de la voie IV',
    'Faire anesthésie locale (lidocaïne 2%) chez patient conscient',
    'En cas d\'échec, ne pas piquer une 2ème fois du même côté',
    'Possibilité prélèvements d\'urgence (tube hépariné)',
    'Surveillance++ de la voie (coloration, induration cutanée)'
  ];

  const totalEtapes = etapesTechnique.length;
  const etapesValidees = Object.values(formData.etapesTechnique).filter(Boolean).length;
  const progression = totalEtapes > 0 ? Math.round((etapesValidees / totalEtapes) * 100) : 0;

  return (
    <div className="p-3 md:p-6 space-y-3 md:space-y-4 max-w-4xl mx-auto">
      {/* Header */}
      <div className="space-y-2 print:hidden">
        <div className="flex flex-col sm:flex-row justify-between gap-2">
          <div className="flex items-center gap-2">
            <Syringe className="w-6 h-6 md:w-8 md:h-8 text-cyan-400" />
            <div>
              <h1 className="text-lg md:text-2xl font-bold text-[hsl(var(--text))]">Voie Intraosseuse</h1>
              <p className="text-xs text-[hsl(var(--text-dim))]">Système motorisé EZ-IO®</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAnalyzeAI} disabled={isAnalyzing} size="sm" className="bg-purple-400/20 text-purple-300 border-purple-400/30">
              {isAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            </Button>
            <Button onClick={handleReset} size="sm" variant="outline" className="text-amber-300 border-amber-400/30">
              <RotateCcw className="w-4 h-4" />
            </Button>
            <Button onClick={handleExportPDF} size="sm" className="bg-cyan-400/20 text-cyan-300 border-cyan-400/30">
              <Download className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Progress */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs md:text-sm">
            <span className="text-[hsl(var(--text-dim))]">Progression technique</span>
            <span className="font-mono font-bold text-cyan-300">{progression}% ({etapesValidees}/{totalEtapes})</span>
          </div>
          <div className="h-2 bg-[hsl(var(--bg-elev))] rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-cyan-400 to-blue-400 transition-all duration-500"
              style={{ width: `${progression}%` }}
            />
          </div>
        </div>

        <Alert className="bg-cyan-400/10 border-cyan-400/30 p-2">
          <Info className="h-4 w-4 text-cyan-400" />
          <AlertDescription className="text-xs text-[hsl(var(--text))]">
            <strong>Voie d'urgence :</strong> Alternative à la VVP en cas d'échec ou état de choc
          </AlertDescription>
        </Alert>
      </div>

      <div ref={printRef} className="space-y-3">
        {/* GÉNÉRALITÉS */}
        <Card className="border-cyan-400/30 bg-cyan-400/5">
          <CardHeader className="p-3">
            <CardTitle className="text-sm md:text-base text-cyan-300 flex items-center gap-2">
              <Info className="w-4 h-4" />
              GÉNÉRALITÉS
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-2">
            <div className="space-y-1.5 text-xs md:text-sm text-[hsl(var(--text))]">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                <span>Voie utilisant les sinusoïdes et les vaisseaux médullaires</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                <span>Trocart introduit au travers de la corticale osseuse par système motorisé</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                <span>Un seul moteur, trois tailles de trocart disponibles</span>
              </div>
            </div>

            <div className="pt-2 border-t border-[hsl(var(--border))]">
              <div className="text-xs font-semibold text-[hsl(var(--text))] mb-2">Tailles de trocart</div>
              <div className="flex flex-wrap gap-2">
                {['Pédiatrique', 'Adulte (>30kg)', 'Obèse'].map((taille) => (
                  <button
                    key={taille}
                    onClick={() => setFormData(prev => ({ ...prev, tailleTrocart: taille }))}
                    className={`px-3 py-1.5 text-xs rounded-lg transition-all ${
                      formData.tailleTrocart === taille
                        ? 'bg-cyan-400 text-black font-semibold'
                        : 'bg-[hsl(var(--bg-elev))] text-[hsl(var(--text-dim))]'
                    }`}
                  >
                    {taille}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* INDICATIONS & CONTRE-INDICATIONS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Card className="border-green-400/30 bg-green-400/5">
            <CardHeader className="p-3">
              <CardTitle className="text-sm md:text-base text-green-300 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                INDICATIONS
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="space-y-2 text-xs md:text-sm text-[hsl(var(--text))]">
                <div className="font-medium text-green-300 mb-2">Mise en place d'un accès vasculaire :</div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Échec de la voie veineuse périphérique</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Faible débit périphérique (état de choc ou ACR)</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-400/30 bg-red-400/5">
            <CardHeader className="p-3">
              <CardTitle className="text-sm md:text-base text-red-300 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                CONTRE-INDICATIONS
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="space-y-2 text-xs md:text-sm text-[hsl(var(--text))]">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <span>Infection cutanée/osseuse au point de ponction</span>
                </div>
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <span>Traumatisme et/ou fracture sur membre concerné</span>
                </div>
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <span>Tumeur cutanée au site</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* SITES D'INSERTION */}
        <Card className="border-blue-400/30 bg-blue-400/5">
          <CardHeader className="p-3">
            <CardTitle className="text-sm md:text-base text-blue-300 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              SITES D'INSERTION
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-3">
            <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">Choisir le site :</div>
            {sites.map((site) => (
              <div
                key={site.id}
                onClick={() => setFormData(prev => ({ ...prev, siteChoisi: site.id }))}
                className={`p-3 rounded-lg cursor-pointer transition-all border ${
                  formData.siteChoisi === site.id
                    ? 'bg-blue-500/20 border-blue-400/50'
                    : 'bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))] hover:border-blue-400/30'
                }`}
              >
                <div className="flex items-start justify-between mb-1.5">
                  <div className="font-semibold text-sm md:text-base text-[hsl(var(--text))]">{site.nom}</div>
                  {formData.siteChoisi === site.id && (
                    <Badge className="bg-blue-500 text-white">Sélectionné</Badge>
                  )}
                </div>
                <div className="text-xs text-blue-300 mb-1">📍 {site.repere}</div>
                <div className="text-xs text-[hsl(var(--text-dim))]">{site.detail}</div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* TECHNIQUE (12 ÉTAPES) */}
        <Card className="border-purple-400/30 bg-purple-400/5">
          <CardHeader className="p-3">
            <CardTitle className="text-sm md:text-base text-purple-300 flex items-center gap-2">
              <Wrench className="w-4 h-4" />
              TECHNIQUE (12 étapes)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-2">
            {etapesTechnique.map((etape) => (
              <div
                key={etape.id}
                onClick={() => handleCheckEtape(etape.id)}
                className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer transition-all ${
                  formData.etapesTechnique[etape.id]
                    ? 'bg-purple-500/20 border border-purple-400/30'
                    : 'bg-[hsl(var(--bg-elev))] hover:bg-[hsl(var(--bg-elev))]/80'
                }`}
              >
                <Checkbox
                  checked={formData.etapesTechnique[etape.id] || false}
                  className="mt-0.5 flex-shrink-0"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs font-mono">{etape.id}</Badge>
                    <span className="text-xs md:text-sm text-[hsl(var(--text))]">{etape.label}</span>
                  </div>
                </div>
                {formData.etapesTechnique[etape.id] && (
                  <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                )}
              </div>
            ))}

            <Alert className="mt-3 bg-amber-500/10 border-amber-500/30 p-2">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              <AlertDescription className="text-xs text-amber-300">
                <strong>Point clé :</strong> Vérifier aspiration de moelle osseuse après insertion
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* COMPLICATIONS & À SAVOIR */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Card className="border-red-400/30 bg-red-400/5">
            <CardHeader className="p-3">
              <CardTitle className="text-sm md:text-base text-red-300 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                COMPLICATIONS (Rares)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="space-y-1.5">
                {complications.map((comp, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-xs md:text-sm">
                    <AlertTriangle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                    <span className="text-[hsl(var(--text))]">{comp}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-blue-400/30 bg-blue-400/5">
            <CardHeader className="p-3">
              <CardTitle className="text-sm md:text-base text-blue-300 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                À SAVOIR
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="space-y-1.5">
                {pointsASavoir.map((point, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-xs md:text-sm">
                    <CheckCircle2 className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span className="text-[hsl(var(--text))]">{point}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* AI ANALYSIS */}
        {aiAnalysis && (
          <Card className="border-purple-500/30 bg-purple-500/5">
            <CardHeader className="p-3">
              <CardTitle className="text-sm md:text-base text-purple-300 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                ANALYSE IA - Recommandations Voie IO
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="prose prose-sm max-w-none">
                <div className="whitespace-pre-wrap text-xs md:text-sm text-[hsl(var(--text))]">
                  {aiAnalysis.analysis}
                </div>
              </div>
              <Alert className="mt-3 bg-amber-400/10 border-amber-400/30 p-2">
                <AlertDescription className="text-xs text-amber-300">
                  <strong>Disclaimer :</strong> Aide à la décision, validation médicale requise
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
